import { Vector } from './vector';

export interface Point {
    x: number;
    y: number;
}

export interface Segment {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

export function closestPointOnSegment(p: Point, seg: Segment): Point {
    const dx = seg.x2 - seg.x1;
    const dy = seg.y2 - seg.y1;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) {
        return { x: seg.x1, y: seg.y1 };
    }
    let t = ((p.x - seg.x1) * dx + (p.y - seg.y1) * dy) / lenSq;
    if (t < 0) t = 0;
    else if (t > 1) t = 1;
    return { x: seg.x1 + t * dx, y: seg.y1 + t * dy };
}

export function pointToSegmentDistance(p: Point, seg: Segment): number {
    const cp = closestPointOnSegment(p, seg);
    const dx = p.x - cp.x;
    const dy = p.y - cp.y;
    return Math.sqrt(dx * dx + dy * dy);
}

export interface WallForceOptions {
    range: number;
    strength: number;
    floor?: number;
}

export function wallForce(p: Point, seg: Segment, opts: WallForceOptions): Vector {
    const cp = closestPointOnSegment(p, seg);
    const dx = p.x - cp.x;
    const dy = p.y - cp.y;
    const distSq = dx * dx + dy * dy;
    const dist = Math.sqrt(distSq);
    if (dist >= opts.range) return new Vector(0, 0);

    const floor = opts.floor ?? 1;
    const effective = dist < floor ? floor : dist;

    if (dist === 0) {
        const sx = seg.x2 - seg.x1;
        const sy = seg.y2 - seg.y1;
        const segLen = Math.sqrt(sx * sx + sy * sy);
        if (segLen === 0) return new Vector(0, 0);
        const nx = -sy / segLen;
        const ny = sx / segLen;
        return new Vector(nx * (opts.strength / floor), ny * (opts.strength / floor));
    }

    const nx = dx / dist;
    const ny = dy / dist;
    const magnitude = opts.strength / effective;
    return new Vector(nx * magnitude, ny * magnitude);
}
